import React, { useEffect, useState } from "react";
import { FiCopy, FiEdit2 } from "react-icons/fi";
import { Button, Input } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { updateStudioId } from "../../services/userIdsApi";
import "./user-ids.scss";

/** Roles that may change Studio IDs (the server checks again, including studio and rank). */
export const canEditStudioIds = (role?: string | null) =>
  ["ORGANIZATION_ADMIN", "ORGANIZATION_MANAGER", "SUPER_ADMIN", "ADMIN"].includes(String(role || "").toUpperCase());

const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

/** HKP-U-000123 with a copy button. Read-only: the number never changes. */
export const HkpId = ({ value }: { value?: string | null }) => {
  if (!value) return <span className="uid-muted">—</span>;
  return (
    <span className="uid">
      <span className="uid-code">{value}</span>
      <button
        type="button"
        className="uid-icon"
        aria-label={`Copy ${value}`}
        title="Copy"
        onClick={async () => {
          const ok = await copyText(value);
          notifications({ title: ok ? "Copied" : "Error", message: ok ? value : "Could not copy", type: ok ? "success" : "danger" });
        }}
      >
        <FiCopy aria-hidden="true" />
      </button>
    </span>
  );
};

/**
 * The person's number in the studio (S_3, or K_175 for a Karkhana worker). Admins and managers
 * change it in place; a clash shows under the field instead of a toast so they can fix it.
 */
export const StudioId = ({
  userId,
  value,
  editable,
  onSaved,
}: {
  userId: string;
  value?: string | null;
  editable: boolean;
  onSaved?: (studioId: string) => void;
}) => {
  const [current, setCurrent] = useState(value || "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCurrent(value || "");
  }, [value]);

  const start = () => {
    setDraft(current);
    setError("");
    setEditing(true);
  };

  const save = async () => {
    const next = draft.trim().toUpperCase();
    if (!next) {
      setError("Enter a Studio ID");
      return;
    }
    if (next === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res: any = await updateStudioId(userId, next);
      if (!res?.success) {
        setError(res?.message || "Could not save the Studio ID");
        return;
      }
      const saved = res?.data?.studioId || next;
      setCurrent(saved);
      setEditing(false);
      onSaved?.(saved);
    } catch {
      setError("Could not reach the server");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span className="uid">
        <span className="uid-code">{current || "—"}</span>
        {editable ? (
          <button type="button" className="uid-icon" aria-label="Change Studio ID" title="Change" onClick={start}>
            <FiEdit2 aria-hidden="true" />
          </button>
        ) : null}
      </span>
    );
  }
  return (
    <span className="uid-edit">
      <span className="uid-edit__row">
        <Input
          bsSize="sm"
          className="uid-code"
          value={draft}
          autoFocus
          aria-label="Studio ID"
          aria-invalid={!!error}
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") setEditing(false);
          }}
        />
        <Button size="sm" className="ResetBtn" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button size="sm" color="secondary" outline disabled={saving} onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </span>
      {error ? (
        <span className="uid-error" role="alert">
          {error}
        </span>
      ) : null}
    </span>
  );
};
